import {
  CheckboxProtector,
  ContainerDetail,
  ContainerItem,
  ContainerLocationStatus,
  HorizontalList,
  VerticalList,
} from "./BoardInventoryStyles";
import Checkbox from "../../common/Checkbox";
import { InputLabel, Pill } from "../../../constants/Styles";
import FlashPCBTooltip from "../../common/FlashPCBTooltip";
import InfoIcon from "../../common/FlashPCBTooltip/InfoIcon";
import Icon from "../../common/Icon";
import Button from "../../common/Button";
import React, { Dispatch, SetStateAction, useState } from "react";
import { Container } from "../../../reducers/componentSearch";
import BinInfo from "./BinInfo";
import { useBoardFromContext } from "../../../utils/hooks/commonly-stored/uploaded-board/board";
import { PartWithInventory } from "../../../utils/hooks/inventory/boardInventory";
import { Board } from "../../../reducers/uploadedBoard";
import ModalContentWithCloseButton from "../../common/ModalContentWithCloseButton";
import Modal from "../../common/Modal";
import AdjustAllocation from "./AdjustAllocation";
import { InventoryBin } from "../parts/Bins/inventoryBinTypes";
import MarkAllocationAsSpent from "./MarkAllocationAsSpent";
import { ContainerModalLink } from "../parts/BarcodeLink/ContainerModalLink";
import _ from "lodash";
import { CONTAINER_TYPE_OPTIONS } from "../../component-search/PartDrawer/Inventory/PhysicalInventory/AddNewContainer";

export default function ContainerInfo({
  part,
  container,
  qtyAllocated,
  requiredAmount,
  boardBin,
  shipmentId,
  selectedContainers,
  setSelectedContainers,
}: {
  part: PartWithInventory;
  container: Container;
  qtyAllocated: number;
  requiredAmount: number;
  boardBin: InventoryBin | null;
  shipmentId: string;
  selectedContainers: Container[];
  setSelectedContainers: Dispatch<SetStateAction<Container[]>>;
}) {
  const board = useBoardFromContext().board as Board;
  // Allocations and counts come from the part, which is mid-update
  const partUpdating = part.part.is_updating;
  const containerIsInBoardBin =
    container.flashpcb_bin_barcode === boardBin?.flashpcb_bin_barcode;
  const [adjustAllocation, setAdjustAllocation] = useState<boolean>(false);
  const [markAllocationAsSpent, setMarkAllocationAsSpent] =
    useState<boolean>(false);
  const containerIsSelected = (container: Container) => {
    return selectedContainers.some(
      (selectedContainer) =>
        selectedContainer.flashpcb_container_barcode ===
        container.flashpcb_container_barcode,
    );
  };

  const updateSelectedContainers = (container: Container) => {
    setSelectedContainers((prevSelectedContainers) => {
      const containerSelected = prevSelectedContainers.some(
        (selectedContainer) =>
          selectedContainer.flashpcb_container_barcode ===
          container.flashpcb_container_barcode,
      );

      if (containerSelected) {
        return prevSelectedContainers.filter(
          (selectedContainer) =>
            selectedContainer.flashpcb_container_barcode !==
            container.flashpcb_container_barcode,
        );
      } else {
        return [...prevSelectedContainers, container];
      }
    });
  };

  const closeModal = () => {
    setAdjustAllocation(false);
    setMarkAllocationAsSpent(false);
  };

  return (
    <>
      <ContainerDetail
        isAllocated={qtyAllocated > 0}
        key={container.flashpcb_container_barcode}
      >
        <CheckboxProtector>
          <Checkbox
            checked={containerIsSelected(container)}
            onChange={() => updateSelectedContainers(container)}
          />
        </CheckboxProtector>

        <VerticalList>
          <ContainerItem>
            <ContainerModalLink
              containerBarcode={container.flashpcb_container_barcode}
            />
          </ContainerItem>
          <ContainerItem>
            <InputLabel>Type: </InputLabel>
            {CONTAINER_TYPE_OPTIONS[container.container_type].label}
          </ContainerItem>
          {qtyAllocated > 0 && (
            <ContainerItem>
              <InputLabel>Amount allocated to this project: </InputLabel>
              {partUpdating ? <Pill width={"3rem"} /> : qtyAllocated}
            </ContainerItem>
          )}
          <HorizontalList>
            <ContainerItem>
              <InputLabel>Last counted amount: </InputLabel>
              {partUpdating ? (
                <Pill width={"3rem"} />
              ) : (
                container.last_counted_amount
              )}
            </ContainerItem>
            <ContainerItem>
              <InputLabel>Active allocations: </InputLabel>
              {partUpdating ? (
                <Pill width={"3rem"} />
              ) : (
                container.active_allocations_total
              )}
            </ContainerItem>
            <ContainerItem>
              <InputLabel>Spent allocations: </InputLabel>
              {partUpdating ? (
                <Pill width={"3rem"} />
              ) : (
                container.spent_allocations_total
              )}
            </ContainerItem>
          </HorizontalList>
          <HorizontalList>
            <ContainerItem>
              <InputLabel>
                Available inventory:{" "}
                <FlashPCBTooltip
                  content={`Last counted amount (${container.last_counted_amount}). Available inventory (${container.available_inventory_level})`}
                >
                  <InfoIcon />
                </FlashPCBTooltip>
              </InputLabel>
              {partUpdating ? (
                <Pill width={"3rem"} />
              ) : (
                container.available_inventory_level
              )}
            </ContainerItem>
            <ContainerItem>
              <InputLabel>Container type:</InputLabel>
              {_.startCase(container.container_type)}
            </ContainerItem>
          </HorizontalList>
          {containerIsInBoardBin ? (
            <ContainerLocationStatus>
              <>
                <Icon
                  src={"/icons/check_green.svg"}
                  alt={"check"}
                  styleProps={{
                    height: "1.5rem",
                    width: "1.5rem",
                    marginRight: "0.5rem",
                    verticalAlign: "middle",
                  }}
                />{" "}
                In board tote
              </>
            </ContainerLocationStatus>
          ) : (
            <ContainerItem>
              <BinInfo
                binName={container.bin_name}
                binLocation={container.bin_location}
                binBarcode={container.flashpcb_bin_barcode}
                binType={container.bin_type}
                canAllocateFromBin={container.can_allocate_from_bin}
              />
            </ContainerItem>
          )}
          <ContainerItem>
            <Button
              label={"Adjust quantity allocated from this container"}
              onClick={() => setAdjustAllocation(true)}
              isSmallSize={true}
              disabled={partUpdating}
            />
          </ContainerItem>
          <ContainerItem>
            <Button
              label={"Spend allocation from this container"}
              onClick={() => setMarkAllocationAsSpent(true)}
              isSmallSize={true}
              disabled={partUpdating}
            />
          </ContainerItem>
        </VerticalList>
      </ContainerDetail>
      {adjustAllocation && (
        <Modal isOpen={adjustAllocation} onRequestClose={closeModal}>
          <ModalContentWithCloseButton closeModal={closeModal}>
            <AdjustAllocation
              container={container}
              quantityAllocatedFromThisContainer={qtyAllocated}
              totalQuantityAllocated={part.amount_allocated}
              requiredAmount={requiredAmount}
              boardId={board.id}
              shipmentId={shipmentId}
              closeModal={closeModal}
            />
          </ModalContentWithCloseButton>
        </Modal>
      )}
      {markAllocationAsSpent && (
        <Modal isOpen={markAllocationAsSpent} onRequestClose={closeModal}>
          <ModalContentWithCloseButton closeModal={closeModal}>
            <MarkAllocationAsSpent
              container={container}
              quantityAllocatedFromThisContainer={qtyAllocated}
              boardId={board.id}
              shipmentId={shipmentId}
              closeModal={closeModal}
            />
          </ModalContentWithCloseButton>
        </Modal>
      )}
    </>
  );
}
